// src/components/ScreenLoading.jsx
// Shown while a screen's code downloads (App.js, around its routes). With no signal that
// download waits for the signal (utils/lazyPage.js), so the screen says so instead of looking
// stuck (09/16/2026, third review round of the phone speed work). It is part of the file every
// launch downloads, so it imports nothing but React and lazyPage.js, which that file holds anyway.
//
// Fourth review round, same day: App also shows it beside a redirect (a signed-in person at
// "/"), which React holds on screen while the next page's file waits. And App's "No signal" bar
// (components/NoSignalBar.jsx) must never repeat this screen's note, so the screen says whenever
// its note is on screen. That is told from a layout effect: it runs before the screen is painted,
// so the bar is already gone in the same frame, and React undoes it when it hides this screen.
//
// Fifth review round, same day: the bar also says "Loading what you opened..." while a page is tried
// with the signal back. This screen already says "Loading...", so it also says whenever it is up at
// all, with or without its note, and the bar leaves that line out meanwhile.
import { useEffect, useLayoutEffect, useState } from "react";
import { browserIsOffline } from "../utils/lazyPage";

// How many of these screens are showing their no-signal note, how many are up at all, and who wants
// to hear when either changes.
let notesOnScreen = 0;
let screensUp = 0;
const noteListeners = new Set();

function tellListeners() {
  noteListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One listener's fault must never break this screen or keep the others from hearing.
    }
  });
}

function changeNotesOnScreen(by) {
  notesOnScreen = Math.max(0, notesOnScreen + by);
  tellListeners();
}

function changeScreensUp(by) {
  screensUp = Math.max(0, screensUp + by);
  tellListeners();
}

export function signalNotesOnScreen() {
  return notesOnScreen;
}

export function loadingScreensOnScreen() {
  return screensUp;
}

// Shaped for React's useSyncExternalStore.
export function subscribeSignalNotes(listener) {
  noteListeners.add(listener);
  return () => {
    noteListeners.delete(listener);
  };
}

// The small "Loading..." a page area shows inside the app (the dashboard's own, around its pages). It is a
// loading screen too, so the bar leaves its own loading line out while this is up (final review of the fifth
// round, 09/16/2026: on a slow start both said "Loading" at once).
export function AreaLoading({ className = "" }) {
  useLayoutEffect(() => {
    changeScreensUp(1);
    return () => changeScreensUp(-1);
  }, []);
  return <div className={className}>Loading...</div>;
}

export default function ScreenLoading() {
  const [offline, setOffline] = useState(browserIsOffline);
  useEffect(() => {
    const update = () => setOffline(browserIsOffline());
    // The signal can change between the first draw and this effect.
    update();
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  useLayoutEffect(() => {
    if (!offline) return undefined;
    changeNotesOnScreen(1);
    return () => changeNotesOnScreen(-1);
  }, [offline]);
  // After the note's effect, so that when the screen goes the note is taken back first.
  useLayoutEffect(() => {
    changeScreensUp(1);
    return () => changeScreensUp(-1);
  }, []);
  return (
    <div className="flex flex-col justify-center items-center h-screen gap-2 px-6 text-center">
      <span>Loading...</span>
      {offline ? (
        <span role="status" className="text-sm text-slate-500">
          No signal. This screen will open when your signal comes back.
        </span>
      ) : null}
    </div>
  );
}
